import { NOTIFICATION_EVENT_TRIGGER_ELEMENT_NAME, NOTIFICATION_EVENT_TRIGGER_NAME } from "components/common/notifications/Notification";
import { createContext } from "react";
import { Roles } from "utilities/app-utilities";
import { INotification } from "../types/notification-type";
import { IUser } from "../types/user-type";
import { uuid } from "./app-constants";
import { LocationManagerData } from "utilities/hooks/useLocationManager";
import { NavigateOptions } from "react-router-dom";
import { appLanguages } from "config/app-constants";

class UserContextType implements IUser {
    name: string;
    id: number;
    username: string;
    roles: string[];

    constructor(name: string, id: number, username: string, roles: string[]) {
        this.name = name;
        this.id = id;
        this.username = username;
        this.roles = roles;
    }

    hasRole(role: string | Roles): boolean {
        return this.roles.indexOf(role) > -1;
    }

    hasRoles(roles: string[] | Roles[]) {
        return roles.every(r => this.hasRole(r));
    }
}

export interface IAppContext {
    user: IUser,
    stateData: Record<string, any>,
}

export class AppContextManager implements IAppContext {
    private locationManagerData: LocationManagerData | undefined = undefined;

    constructor(private state: IAppContext,
        private setState: (i: Partial<IAppContext>) => void) {
    }

    get user() {
        return this.state.user;
    }

    set user(i: IUser) {
        this.update({
            stateData: this.state.stateData,
            user: new UserContextType(i.name, i.id, i.username, i.roles)
        });
    }

    get stateData() {
        return this.state.stateData;
    }

    set stateData(i: Record<string, any>) {
        this.update({
            user: this.state.user,
            stateData: i
        });
    }

    get language() {
        const langCode = document.getElementsByTagName("html")[0].getAttribute("lang") || 'en';
        return appLanguages.find(x => x.code === langCode) || appLanguages[0];
    }

    get modal() {
        return this.locationManagerData!.modal;
    }

    get searchParams() {
        return this.locationManagerData!.searchParams;
    }

    t = (key: string, params?: Record<string, string | number>) => {
        let tr = this.locationManagerData!.t(key, params);
        if(params) {
            for(let i in params) {
                tr = tr.replace(`{${i}}`, params[i]!.toString());
            }
        }

        return tr;
    }

    addStateData = (key: string, value: any) => {
        const newStateData = {
            ...this.stateData,
            [key]: value
        };
        this.setState({
            stateData: newStateData
        });
    }

    removeStateData = (key: string) => {
        const newStateData = {
            ...this.stateData
        };
        delete newStateData[key];
        this.setState({
            stateData: newStateData
        });
    }

    navigate = (route: string | number, params?: Record<string, string | number>, navigateOptions?: NavigateOptions) => {
        this.locationManagerData!.navigate(route, params, navigateOptions);
    }

    setLocationManagerData = (i: LocationManagerData) => {
        this.locationManagerData = i;
    }

    screen = () => {
        if (window.innerWidth <= 640) return 'sm';
        if (window.innerWidth <= 768) return 'md';
        if (window.innerWidth <= 1024) return 'lg';
        if (window.innerWidth <= 1280) return 'xl';
        if (window.innerWidth <= 1536) return '2xl';
    }

    sendUpdateEvent = (eventName: string, data?: Record<string, any>) => {
        const triggerEvent = new CustomEvent(eventName, {
            bubbles: true,
            cancelable: false,
            composed: true,
            detail: data || {}
        });
        document.getElementById("root")?.dispatchEvent(triggerEvent);
    }

    notify = (type: INotification['type'], message: string, description?: string) => {
        const notification = {
            created: Number(new Date()),
            message,
            description,
            type,
            id: uuid()
        };

        const triggerEvent = new CustomEvent(NOTIFICATION_EVENT_TRIGGER_NAME, {
            bubbles: true,
            cancelable: false,
            composed: true,
            detail: notification
        });
        document.getElementById(NOTIFICATION_EVENT_TRIGGER_ELEMENT_NAME)!.dispatchEvent(triggerEvent)
    }

    // #region permissions  
    userHasRole(role: string | Roles) {
        if (this.user.roles.indexOf(Roles.ADMIN) > -1) return true;
        return this.user.roles.indexOf(role) > -1;
    }

    userHasRoles(roles: string[] | Roles[]) {
        if (this.user.roles.indexOf(Roles.ADMIN)) return true;
        return roles.every(r => this.userHasRole(r));
    }

    userContainsRoles(roles: string[] | Roles[]) {
        if (this.user.roles.indexOf(Roles.ADMIN)) return true;
        return roles.some(r => this.userHasRole(r));
    }    
    // #endregion permissions   

    private update = (i: Partial<IAppContext>) => {
        this.setState({
            ...this.state,
            ...i
        });
    }
}

export const AppContext = createContext<AppContextManager>(new AppContextManager({
    user: new UserContextType('', 0, '', []),
    stateData: {}
}, i => { }));


export function getAppContext(use_context: any) {
    return use_context(AppContext) as AppContextManager;
}
